import type { ELK, ElkNode, LayoutOptions } from 'elkjs/lib/elk-api'

import { LayoutResult } from './auto-layout.types'

/**
 * Defaults shared by the integration designer and process designer canvases: a left-to-right
 * layered (Sugiyama) layout with orthogonal routing. Canvas adapters override spacing and add
 * per-node / per-port options (port order for fork conditions, first/last layer for START/END,
 * row seeds from row-ranks.ts).
 *
 * Cycle breaking is DEPTH_FIRST, not ELK's default GREEDY: both canvases flow out of a START,
 * so a depth-first walk from the sources reverses only true back edges (retry loops, rework
 * paths). GREEDY can instead reverse the forward edge into a loop and pull the loop body left of
 * the node that enters it.
 */
export const DEFAULT_ELK_LAYERED_OPTIONS: LayoutOptions = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.layered.cycleBreaking.strategy': 'DEPTH_FIRST',
  'elk.edgeRouting': 'ORTHOGONAL',
  // Keeps each column's nodes in the order of their seeds (`elk.position`, the row rank) while
  // still minimizing crossings, so branches keep the rows they were ranked on
  'elk.layered.crossingMinimization.semiInteractive': 'true',
  // Network simplex honours per-edge straightness priorities, which keep each row straight
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
  'elk.layered.spacing.nodeNodeBetweenLayers': '120',
  'elk.spacing.nodeNode': '60',
}

/**
 * Network simplex placement grows roughly with the square of the drawing: past this many nodes it
 * would take over a second, so larger drawings fall back to ELK's default placement, which is
 * fast but ignores the straightness priorities (rows are then only as straight as it makes them).
 */
export const STRAIGHT_PLACEMENT_NODE_LIMIT = 200

/**
 * A canvas's own `options` for a drawing of `nodeCount` nodes, with the node placement it can
 * afford: network simplex, or ELK's default placement past STRAIGHT_PLACEMENT_NODE_LIMIT.
 */
export const layeredLayoutOptions = (nodeCount: number, options: LayoutOptions): LayoutOptions => ({
  ...options,
  'elk.layered.nodePlacement.strategy':
    nodeCount > STRAIGHT_PLACEMENT_NODE_LIMIT ? 'BRANDES_KOEPF' : 'NETWORK_SIMPLEX',
})

/**
 * Lazily `import()`ed into its own chunk: elkjs is ~465 KB gzipped and only needed when the user
 * asks for an auto-layout, so the canvases never pay for it on load. One ELK instance is kept for
 * the session; a failed load is not cached so the next request retries.
 */
let elkInstance: Promise<ELK> | null = null

function loadElk(): Promise<ELK> {
  elkInstance ??= import('elkjs/lib/elk.bundled.js')
    .then(({ default: ElkConstructor }) => new ElkConstructor())
    .catch((error: unknown) => {
      elkInstance = null
      throw error
    })

  return elkInstance
}

/**
 * Runs ELK on a flat graph (the root's direct children) and returns their new top-left
 * positions. Options precedence: shared defaults < the graph's own `layoutOptions` < `options`.
 */
export async function runElkLayout(
  graph: ElkNode,
  options: LayoutOptions = {}
): Promise<LayoutResult> {
  const elk = await loadElk()
  const laidOut = await elk.layout({
    ...graph,
    layoutOptions: { ...DEFAULT_ELK_LAYERED_OPTIONS, ...graph.layoutOptions, ...options },
  })

  return toLayoutResult(laidOut)
}

/** Positions are rounded: both canvases persist integer coordinates. */
export function toLayoutResult(laidOut: ElkNode): LayoutResult {
  return {
    positions: new Map(
      (laidOut.children ?? []).map((child) => [
        child.id,
        { x: Math.round(child.x ?? 0), y: Math.round(child.y ?? 0) },
      ])
    ),
  }
}
